import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The editor of a scheduled message.
 *
 * The recurrence compartments show according to the mode: displaying the seven
 * weekdays to somebody who chose "once" leaves them doubting what will actually
 * be taken into account.
 */

interface Schedule {
  _id: string;
  name: string;
  channelId: string;
  content: string;
  useEmbed: boolean;
  embed?: { title: string; description: string; color: string; imageUrl: string };
  mentionRoleId: string | null;
  mode: string;
  runAt: string | null;
  hour: number;
  minute: number;
  weekdays: number[];
  intervalHours: number;
  antiFlood: boolean;
  deletePrevious: boolean;
  enabled: boolean;
}

interface EditorPageData {
  schedule: Schedule;
  guildId: string;
}

export function initScheduledEditorPage(): void {
  const form = $<HTMLFormElement>('[data-scheduled-form]');
  if (!form) return;

  const { schedule, guildId } = pageData<EditorPageData>();

  fillForm(form, schedule as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = schedule.channelId ? [schedule.channelId] : [];

  const mention = findSelectMenu(form, 'mentionRoleId');
  if (mention) mention.value = schedule.mentionRoleId ? [schedule.mentionRoleId] : [];

  for (const box of $$<HTMLInputElement>('[data-weekday]', form)) {
    box.checked = schedule.weekdays.includes(Number(box.dataset.weekday));
  }

  /*
   * `fillForm` can guess nothing for a creation: three unticked boxes would
   * make a schedule that is off, with no anti-flood — which the guide describes
   * as recommended.
   */
  const fresh = !schedule._id;
  toggle(form, 'enabled', fresh ? true : schedule.enabled);
  toggle(form, 'antiFlood', fresh ? true : schedule.antiFlood);
  toggle(form, 'deletePrevious', fresh ? false : schedule.deletePrevious);
  toggle(form, 'useEmbed', fresh ? false : schedule.useEmbed);

  fillEmbed(form, schedule.embed);

  const useEmbed = form.elements.namedItem('useEmbed');
  if (useEmbed instanceof HTMLInputElement) {
    useEmbed.addEventListener('change', () => {
      showEmbed(form, useEmbed.checked);
    });
    showEmbed(form, useEmbed.checked);
  }

  const mode = form.elements.namedItem('mode');
  if (mode instanceof HTMLSelectElement) {
    mode.value = schedule.mode;
    mode.addEventListener('change', () => {
      showFor(form, mode.value);
    });
  }
  showFor(form, schedule.mode);

  mountResourceEditor({
    endpoint: 'scheduled',
    listUrl: `/modules/scheduled?guildId=${guildId}`,
    guildId,
    form,
    itemId: schedule._id,

    serialize: () => {
      const chosen = $$<HTMLInputElement>('[data-weekday]', form)
        .filter((box) => box.checked)
        .map((box) => Number(box.dataset.weekday));

      const runAt = text(form, 'runAt');

      return {
        name: text(form, 'name'),
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        content: text(form, 'content'),
        useEmbed: checked(form, 'useEmbed'),
        embed: {
          title: text(form, 'embedTitle'),
          description: text(form, 'embedDescription'),
          color: text(form, 'embedColor'),
          imageUrl: text(form, 'embedImage'),
        },
        mentionRoleId: findSelectMenu(form, 'mentionRoleId')?.single || null,
        mode: choice(form, 'mode'),
        // `datetime-local` returns a local time with no zone; `Date` reads it in
        // the browser's, which is exactly what the person typed.
        runAt: runAt ? new Date(runAt).toISOString() : null,
        hour: Number(text(form, 'hour')) || 0,
        minute: Number(text(form, 'minute')) || 0,
        weekdays: chosen,
        intervalHours: Number(text(form, 'intervalHours')) || 24,
        antiFlood: checked(form, 'antiFlood'),
        deletePrevious: checked(form, 'deletePrevious'),
        enabled: checked(form, 'enabled'),
        publish: false,
      };
    },

    labels: {
      saved: ct('scheduleSaved', 'Scheduled message saved.'),
      confirmDelete: ct('confirmDeleteSchedule', 'Delete this scheduled message?'),
      deleted: ct('scheduleDeleted', 'Scheduled message deleted.'),
    },
  });
}

/**
 * The embed is stored as a sub-document but entered as flat fields: `fillForm`
 * does not descend into objects, and a saved embed would come back empty on
 * reopening the page — which reads as data loss.
 */
function fillEmbed(form: HTMLFormElement, embed: Schedule['embed']): void {
  const values: Record<string, string> = {
    embedTitle: embed?.title ?? '',
    embedDescription: embed?.description ?? '',
    // `type="color"` refuses an empty value and falls back to black: Discord's
    // blue is a more honest default than a colour nobody chose.
    embedColor: embed?.color || '#5865F2',
    embedImage: embed?.imageUrl ?? '',
  };

  for (const [name, value] of Object.entries(values)) {
    const field = form.elements.namedItem(name);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      field.value = value;
    }
  }
}

function showEmbed(form: HTMLFormElement, on: boolean): void {
  const block = $<HTMLElement>('[data-embed-fields]', form);
  if (block) block.hidden = !on;
}

/** Shows only the compartments the chosen mode actually uses. */
function showFor(form: HTMLFormElement, mode: string): void {
  const visible: Record<string, string[]> = {
    once: ['once'],
    daily: ['clock'],
    weekly: ['clock', 'weekly'],
    interval: ['interval'],
  };

  const wanted = visible[mode] ?? [];
  for (const block of $$<HTMLElement>('[data-when]', form)) {
    block.hidden = !wanted.includes(block.dataset.when ?? '');
  }
}

function toggle(form: HTMLFormElement, name: string, on: boolean): void {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLInputElement) field.checked = on;
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function choice(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLSelectElement ? field.value : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
